import {useDispatch} from 'react-redux';
import { mergeRoadmapNamesById } from '../redux/slices/settings';
import {
    appendRoadmaps,
    resetRoadmapsPagination,
    setProjectMembers,
    setRoadmap,
    setRoadmapError,
    setRoadmapLoading,
    setRoadmaps,
    setRoadmapsError,
    setRoadmapsLoading,
    setRoadmapsLoadingMore,
    setSaveRoadmap,
    setSaveRoadmapError,
    setSaveRoadmapLoading,
    setSingleRoadmap,
    setSingleRoadmapError,
    setSingleRoadmapLoading,
    setProjectApiPayload
} from '../redux/slices/roadmap';
import {setHasSimulationTasks, setTaskList, setTaskListLoading} from '../redux/slices/projects';
import axios from '../utils/axiosConfig';
import {errorHandler} from '../utils/errorHandler';
import store from '../redux/store';
import {resetProjectName, setProjectIdForScroll} from "../redux/slices/timeline";
import { buildProjectRow, mapTasksWithOverlay, buildNormalized, mapTasksSimple } from './mappers/roadmapMapper';
import toast from "react-hot-toast";


const useRoadmapService = (project_id="") => {
    const dispatch = useDispatch();

    const showError = (error: any) => {
        errorHandler.showError(error);
    };

    // Helper function to check if simulation tasks exist (new combined API)
    const checkSimulationTasks = async (project_id: string): Promise<boolean> => {
        try {
            const response = await axios.post(`/simulation/${project_id}/data?force_refresh=true`, {
                // response_type: "gantt"
            });
            const simTasks = response?.data?.sim_data?.tasks || [];
            return Array.isArray(simTasks) && simTasks.length > 0;
        } catch (error) {
            showError(error);
            return false;
        }
    };

    const fetchData = async (project_id: any,sim_flag?:boolean,query?:any, force_refresh: boolean = false ) => {
        if(!project_id) return 
        const API_URL = `/simulation/${project_id}/data${force_refresh ? '?force_refresh=true' : ''}`

        // If we already have the combined payload, avoid network request and just re-shape on toggle
        const { projectApiPayload, projectApiPayloadProjectId } = store.getState().roadmap || {} as any;
        const isSameProject = projectApiPayloadProjectId === project_id;
        const cached = isSameProject ? projectApiPayload : null;
        if (cached && !force_refresh) {
            try {
                const raw = cached;
                const source = sim_flag ? (raw.sim_data || {}) : raw;
                const projectRow = buildProjectRow(raw, source, project_id, sim_flag);
                const tasksFromApi = mapTasksWithOverlay(raw, sim_flag);
                const normalized = buildNormalized(raw, source, projectRow, tasksFromApi);
                dispatch(setRoadmap(normalized));
                dispatch(setProjectMembers(raw.members));
                // Also update hasSimulationTasks based on cached payload when in live mode
                if (!sim_flag) {
                    const liveTasks = Array.isArray(raw.tasks) ? raw.tasks : [];
                    if (liveTasks.length <= 0) {
                        const hasSimTasks = !!(raw?.sim_data?.tasks && raw.sim_data.tasks.length > 0);
                        dispatch(setHasSimulationTasks(hasSimTasks));
                    } else {
                        dispatch(setHasSimulationTasks(false));
                    }
                }
                return;
            } catch (e) {
                // fallthrough to network request
            }
        }

        dispatch(setRoadmapLoading(true));
        dispatch(resetProjectName())
        try {
            const response = await axios.post(API_URL,{
                ...query,
                // response_type:"gantt"
            });

            const raw = response.data || {};
            // cache the raw combined payload for instant toggle without refetch
            try { dispatch(setProjectApiPayload({ payload: raw, projectId: project_id })); } catch (e) {}

            // Pick data based on simulation flag
            const source = sim_flag ? (raw.sim_data || {}) : raw;

            // Build a synthetic project row for index 0 to preserve existing UI expectations
            const projectRow = buildProjectRow(raw, source, project_id, sim_flag);
            const tasksFromApi = mapTasksWithOverlay(raw, sim_flag);
            const normalized = buildNormalized(raw, source, projectRow, tasksFromApi);

            dispatch(setRoadmap(normalized));
            dispatch(setProjectMembers(raw.members));

            // If we're in live mode and we have no tasks, check if simulation has tasks
            if (!sim_flag) {
                const liveTasks = Array.isArray(raw.tasks) ? raw.tasks : [];
                if (liveTasks.length <= 0) {
                    const hasSimTasks = await checkSimulationTasks(project_id);
                    dispatch(setHasSimulationTasks(!!hasSimTasks));
                } else {
                    dispatch(setHasSimulationTasks(false));
                }
            }
        } catch (error) {
            showError(error)
            dispatch(setRoadmap({ Results: [] }));
            dispatch(setRoadmapError(error));
        } finally {
            dispatch(setRoadmapLoading(false));
        }
    };

    const fetchProjectShareData = async (token: any,sim_flag?:boolean,query?:any ) => {
        if(!token) return 
        const API_URL = `/project/share`
        dispatch(setRoadmapLoading(true));
        dispatch(setTaskListLoading(true));
        try {
            const response = await axios.post(API_URL,{
                response_type:"gantt",
                ...query,
                token,
                sim_flag:sim_flag ? sim_flag : false,
            },
            {
                ignoreGlobalCatch: true,
            } as any);
            const raw = response.data || {};
            const source = sim_flag ? (raw.sim_data || {}) : raw;

            const projectRow = buildProjectRow(raw, source, '', sim_flag);
            const tasksFromApi = mapTasksSimple(raw, sim_flag);
            const normalized = buildNormalized(raw, source, projectRow, tasksFromApi);

            dispatch(setRoadmap(normalized));
            dispatch(setTaskList({ tasks: tasksFromApi }));
            // dispatch(setRoadmapCPLI(response.data));
        } catch (error) {
            showError(error)
            dispatch(setRoadmap({ Results: [] }));
            dispatch(setRoadmapError(error));
        } finally {
            dispatch(setRoadmapLoading(false));
            dispatch(setTaskListLoading(false));
        }
    };


    const fetchRoadmaps = async (
      reset: boolean = true,
      pageNumber?: number,
      searchTerm: string = '',
      searchType: string = 'roadmap',
      sort_map = "",
      source = ""
    ) => {

        return;
        let responseData;
        if (reset) {
            if (pageNumber) {
                // If a specific page is requested, don't reset pagination state
                dispatch(setRoadmapsLoading(true));
            } else {
                dispatch(resetRoadmapsPagination());
                dispatch(setRoadmapsLoading(true));
            }
        } else {
            dispatch(setRoadmapsLoadingMore(true));
        }
        
        try {
            // Use fixed perPage - don't read from Redux as it may be polluted by start_date_desc response
            const perPage = 10;
            const { searchTerm: currentSearchTerm = '' } = store.getState().roadmap;
            const search = searchTerm || currentSearchTerm;
            const currentPage = reset 
                ? (pageNumber || 1) // Use the specified page number or default to 1
                : store.getState().roadmap.page + 1;
            
            // Include search parameter in API request
            const response = await axios.get(
                `/roadmaps?page=${currentPage}&per_page=${perPage}&search=${encodeURIComponent(search)}&search_type=${searchType}&sort_map=${sort_map}&source=${source}`
            );
            
            if (reset) {
                dispatch(setRoadmaps(response.data));
            } else {
                dispatch(appendRoadmaps(response.data));
            }
            // Populate global ID->name cache for roadmaps
            try {
                const roadmaps: any[] = (response as any)?.data?.roadmaps || [];
                if (Array.isArray(roadmaps) && roadmaps.length > 0) {
                    const mapped = roadmaps.reduce<Record<string, string>>((acc, r) => {
                        const id = String(r?.id ?? r?.roadmap_id ?? '');
                        if (!id) return acc;
                        const name = String(r?.name ?? r?.roadmap_name ?? id);
                        acc[id] = name;
                        return acc;
                    }, {});
                    if (Object.keys(mapped).length > 0) {
                        dispatch(mergeRoadmapNamesById(mapped));
                    }
                }

                responseData = response.data;
            } catch (e) { /* ignore */ }
        } catch (error) {
            showError(error)
            dispatch(setRoadmapsError(error));
        } finally {
            if (reset) {
                dispatch(setRoadmapsLoading(false));
            } else {
                dispatch(setRoadmapsLoadingMore(false));
            }
            
        }
        return responseData;
    };

    const fetchMoreRoadmaps = async (org_id?: any, searchType: string = 'roadmap') => {
        const { loadingMore, hasMore, searchTerm = '' } = store.getState().roadmap;

        if (!loadingMore && hasMore) {
            await fetchRoadmaps(false, undefined, searchTerm, searchType);
        }
    };

    const fetchSingleRoadmap = async (roadmap_id?: any,sim_flag?:any, force_refresh: boolean = false, projectIdForScroll: boolean = false) => {
        dispatch(setSingleRoadmapLoading(true));
        try {
            const response =  await axios.get(`/roadmap/${roadmap_id}/${sim_flag}${force_refresh ? '?force_refresh=true' : ''}`);
            dispatch(setSingleRoadmap(response.data));

            if (projectIdForScroll){
              setTimeout(() => {
                const clone = store.getState().timeline.projectIdForScroll;
                dispatch(setProjectIdForScroll(clone));
              }, 20);
            }
        } catch (error) {
            showError(error)
            dispatch(setSingleRoadmapError(error));
        } finally {
            dispatch(setSingleRoadmapLoading(false));
        }
    };

    const downloadRoadmapReport = async (roadmap_id?: any,sim_flag?:any,roadmap_name?:any) => {
        dispatch(setSingleRoadmapLoading(true));
        try {
            const response =  await axios.get(`/roadmap/${roadmap_id}/report/${sim_flag}`,
                {
                    headers:
                    {
                        'Content-Disposition': "attachment; filename=template.xlsx",
                        'Content-Type': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                    },
                    responseType: 'arraybuffer',
                }
            );
            let blob = new Blob([response.data]);
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${roadmap_name} ${sim_flag ? "Simulation" : ""} - Weekly skill requirement by hours - ${+new Date()}.xlsx`);
            link.click();
        } catch (error) {
            showError(error)
            dispatch(setSingleRoadmapError(error));
        } finally {
            dispatch(setSingleRoadmapLoading(false));
        }
    };

    const saveRoadmap = async (params?:any) => {
        dispatch(setSaveRoadmapLoading(true));
        try {
            const response =  await axios.post(`/roadmap`,params);
            toast.success(response.data.message);
            await fetchRoadmaps();
            dispatch(setSaveRoadmap(response.data));
            dispatch(setSaveRoadmapLoading(false));

            return response.data;
        } catch (error) {
            showError(error)
            dispatch(setSaveRoadmapError(error));
            throw error;
        } finally {
            dispatch(setSaveRoadmapLoading(false));
        }
    };
    const updateRoadmap = async (roadmap_id?:any,params?:any) => {
        dispatch(setSaveRoadmapLoading(true));
        try {
            const response =  await axios.put(`/roadmap/${roadmap_id}`,params);
            toast.success(response.data.message);
            // await fetchRoadmaps();
            dispatch(setSaveRoadmap(response.data));
        } catch (error) {
            showError(error)
            dispatch(setSaveRoadmapError(error));
        } finally {
            dispatch(setSaveRoadmapLoading(false));
        }
    };

    const deleteRoadmap = async (roadmap_id?:any) => {
        dispatch(setSaveRoadmapLoading(true));
        try {
            await axios.delete(`/roadmap/${roadmap_id}`);
            await fetchRoadmaps();
            // dispatch(setSaveRoadmap(response.data));
        } catch (error) {
            showError(error)
            dispatch(setSaveRoadmapError(error));
        } finally {
            dispatch(setSaveRoadmapLoading(false));
        }
    };

    const fetchRoadmapById = async (roadmap_id: string) => {
        try {
            const response = await axios.get(`/roadmap/${roadmap_id}`);
            return response.data;
        } catch (error) {
        } finally{
        }
    };

    return { 
        fetchRoadmapById,
        fetchData,
        fetchRoadmaps,
        fetchMoreRoadmaps,
        saveRoadmap,
        deleteRoadmap,
        updateRoadmap,
        fetchSingleRoadmap,
        downloadRoadmapReport,
        fetchProjectShareData,
    };
};

export default useRoadmapService;

export const getRoadmapDetails = async (roadmap_id: string, sim_flag: boolean = false, force_refresh: boolean = false) => {
    try {
        const response = await axios.get(`/roadmap/${roadmap_id}/${sim_flag}${force_refresh ? '?force_refresh=true' : ''}`);
        return response.data;
    } catch (error) {
        console.error("Error fetching roadmap details:", error);
        throw error;
    }
};

